import { useState, useCallback } from "react"
import type { CalibrationPoint } from "@/types/device"

interface UseCalibrationReturn {
  points: CalibrationPoint[]
  addPoint: (rawSg: number, referenceSg: number, temperature: number) => void
  removePoint: (index: number) => void
  clearPoints: () => void
  setPoints: (points: CalibrationPoint[]) => void
  calculateCorrectedSg: (rawSg: number) => number
}

export function useCalibration(
  initialPoints: CalibrationPoint[] | null = null
): UseCalibrationReturn {
  const [points, setPoints] = useState<CalibrationPoint[]>(initialPoints ?? [])

  const addPoint = useCallback(
    (rawSg: number, referenceSg: number, temperature: number) => {
      const newPoint: CalibrationPoint = {
        raw_sg: rawSg,
        reference_sg: referenceSg,
        temperature,
        captured_at: new Date().toISOString(),
      }
      setPoints((prev) => [...prev, newPoint])
    },
    []
  )

  const removePoint = useCallback((index: number) => {
    setPoints((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const clearPoints = useCallback(() => {
    setPoints([])
  }, [])

  /**
   * Calculate corrected specific gravity using linear interpolation
   * between calibration points
   */
  const calculateCorrectedSg = useCallback(
    (rawSg: number): number => {
      if (points.length === 0) return rawSg
      if (points.length === 1) {
        // Single point offset correction
        const offset = points[0].reference_sg - points[0].raw_sg
        return rawSg + offset
      }

      // Sort points by raw SG
      const sorted = [...points].sort((a, b) => a.raw_sg - b.raw_sg)

      // Find the two nearest points for interpolation
      let lowerIdx = 0
      let upperIdx = sorted.length - 1

      for (let i = 0; i < sorted.length; i++) {
        if (sorted[i].raw_sg <= rawSg) {
          lowerIdx = i
        }
        if (sorted[i].raw_sg >= rawSg && i < upperIdx) {
          upperIdx = i
          break
        }
      }

      // If rawSg is outside the range, use the nearest single point
      if (lowerIdx === upperIdx) {
        const offset = sorted[lowerIdx].reference_sg - sorted[lowerIdx].raw_sg
        return rawSg + offset
      }

      // Linear interpolation
      const lower = sorted[lowerIdx]
      const upper = sorted[upperIdx]
      const t = (rawSg - lower.raw_sg) / (upper.raw_sg - lower.raw_sg)
      const lowerOffset = lower.reference_sg - lower.raw_sg
      const upperOffset = upper.reference_sg - upper.raw_sg
      const interpolatedOffset = lowerOffset + t * (upperOffset - lowerOffset)

      return rawSg + interpolatedOffset
    },
    [points]
  )

  return {
    points,
    addPoint,
    removePoint,
    clearPoints,
    setPoints,
    calculateCorrectedSg,
  }
}
